"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.convertPotentialWeb3ProtocolURLToWeb2URL = exports.isYoutubeVideoURL = exports.getManifoldGalleryBaseURL = exports.getEtherscanURLForWallet = exports.getEtherscanURLForContract = exports.getEtherscanURLForAddress = exports.getEtherscanURLForTransactionHash = exports.getEtherscanURLForToken = exports.getEtherscanBaseURL = exports.getOpenSeaTokenURL = exports.getOpenSeaCollectionURL = exports.getOpenSeaBaseURL = void 0;
const network_1 = require("./network");
/**
 * Calculate the OpenSea Base URL based on network, no trailing slash
 * @param networkId
 */
function getOpenSeaBaseURL(networkId) {
    const baseURL = (0, network_1.isTestnet)(networkId) ? 'https://testnets.opensea.io' : 'https://opensea.io';
    let networkName = (0, network_1.getNetworkNameforNetworkId)(networkId);
    if (networkName === 'mainnet') {
        // @important: they use 'ethereum' to mean mainnet
        networkName = 'ethereum';
    }
    return `${baseURL}/assets/${networkName}`;
}
exports.getOpenSeaBaseURL = getOpenSeaBaseURL;
/**
 * Calculate the OpenSea URL for a given collection on a given network.
 * @param networkId
 * @param contractAddress
 * @returns URL
 */
function getOpenSeaCollectionURL(networkId, contractAddress) {
    return `${getOpenSeaBaseURL(networkId)}/${contractAddress}`;
}
exports.getOpenSeaCollectionURL = getOpenSeaCollectionURL;
/**
 * Calculate the OpenSea URL for a given token on a given network.
 * @param networkId
 * @param contractAddress
 * @param tokenId
 * @returns URL
 */
function getOpenSeaTokenURL(networkId, contractAddress, tokenId) {
    return `${getOpenSeaBaseURL(networkId)}/${contractAddress}/${tokenId}`;
}
exports.getOpenSeaTokenURL = getOpenSeaTokenURL;
/**
 * Calculate the Etherscan Base URL based on network, no trailing slash
 *
 * @param networkId
 * @returns URL
 */
function getEtherscanBaseURL(networkId) {
    switch (networkId) {
        case network_1.NETWORK_IDS.OPTIMISM:
            return 'https://optimistic.etherscan.io';
        case network_1.NETWORK_IDS.GOERLI:
            return 'https://goerli.etherscan.io';
        case network_1.NETWORK_IDS.SEPOLIA:
            return 'https://sepolia.etherscan.io';
        case network_1.NETWORK_IDS.BASE:
            return 'https://basescan.org';
        case network_1.NETWORK_IDS.MAINNET:
            return 'https://etherscan.io';
        default:
            throw new Error('Unsupported Network passed to getEtherscanBaseURL');
    }
}
exports.getEtherscanBaseURL = getEtherscanBaseURL;
/**
 * Calculate the Etherscan URL for a given token on a given network.
 * @param networkId
 * @param contractAddress
 * @param tokenId
 * @returns URL
 */
function getEtherscanURLForToken(networkId, contractAddress, tokenId) {
    return `${getEtherscanBaseURL(networkId)}/nft/${contractAddress}/${tokenId}`;
}
exports.getEtherscanURLForToken = getEtherscanURLForToken;
/**
 * Calculate the Etherscan URL for a given transaction on a given network.
 * @param networkId
 * @param transactionHash
 * @returns URL
 */
function getEtherscanURLForTransactionHash(networkId, transactionHash) {
    return `${getEtherscanBaseURL(networkId)}/tx/${transactionHash}`;
}
exports.getEtherscanURLForTransactionHash = getEtherscanURLForTransactionHash;
/**
 * Calculate the Etherscan URL for a given address.
 * @param networkId
 * @param contractAddress
 * @returns URL
 */
function getEtherscanURLForAddress(networkId, address) {
    return `${getEtherscanBaseURL(networkId)}/address/${address}`;
}
exports.getEtherscanURLForAddress = getEtherscanURLForAddress;
/**
 * Calculate the Etherscan URL for a given contract on a given network.
 * @param networkId
 * @param contractAddress
 * @returns URL
 */
function getEtherscanURLForContract(networkId, address) {
    return getEtherscanURLForAddress(networkId, address);
}
exports.getEtherscanURLForContract = getEtherscanURLForContract;
/**
 * Calculate the Etherscan URL for a given wallet on a given network.
 * @param networkId
 * @param contractAddress
 * @returns URL
 */
function getEtherscanURLForWallet(networkId, address) {
    return getEtherscanURLForAddress(networkId, address);
}
exports.getEtherscanURLForWallet = getEtherscanURLForWallet;
/**
 *  Calculate Manifold Gallery prefix based on network, no trailing slash
 * @param networkId
 * @returns URL
 */
function getManifoldGalleryBaseURL(networkId) {
    if (networkId === 1) {
        return 'https://gallery.manifold.xyz';
    }
    return `https://gallery.manifold.xyz/${(0, network_1.getNetworkNameforNetworkId)(networkId)}`;
}
exports.getManifoldGalleryBaseURL = getManifoldGalleryBaseURL;
/**
 * Checks if url is hosted by Youtube, therefore probably a Youtube video.
 *
 * @param url any url string, preferably a video
 * @returns true if is hosted on youtube, false otherwise
 */
function isYoutubeVideoURL(url) {
    return (url.includes('https://www.youtube.com') ||
        url.includes('https://youtu.be') ||
        url.includes('https://youtube.com') ||
        url.includes('https://www.youtube-nocookie.com'));
}
exports.isYoutubeVideoURL = isYoutubeVideoURL;
/**
 * Takes a Media URL, and if it uses ar:// or ipfs:// fixes it to work with web2.
 * Can also handle OpenSea API URLs that utilize EIP1155-compliant swappable `{id}` within the URL.
 *
 * @param url a url that may or may not have a web3 protocol
 * @param id optional id to replace in the url if it is an EIP1155-compliant OpenSea API URL
 * @returns url with web3 protocol converted to web2 protocol https://
 */
const convertPotentialWeb3ProtocolURLToWeb2URL = (url, id) => {
    let optimizedUrl = url;
    if (optimizedUrl.includes('ipfs://ipfs/')) {
        optimizedUrl = optimizedUrl.replace('ipfs://ipfs/', 'https://ipfs.io/ipfs/');
    }
    else if (optimizedUrl.includes('ipfs://')) {
        optimizedUrl = optimizedUrl.replace('ipfs://', 'https://ipfs.io/ipfs/');
    }
    else if (optimizedUrl.includes('ar://')) {
        optimizedUrl = optimizedUrl.replace('ar://', 'https://arweave.net/');
    }
    else if (id &&
        optimizedUrl.includes('api.opensea.io/api/v1/metadata/') &&
        optimizedUrl.includes('{id}')) {
        // OpenSea API URL utilizes EIP1155 swappable `{id}` within the URL.
        // Convert the id to a hexstring before replacing.
        let hexTokenId = id.toHexString();
        hexTokenId = hexTokenId.replace('0x', '');
        optimizedUrl = optimizedUrl.replace('{id}', hexTokenId);
    }
    return optimizedUrl;
};
exports.convertPotentialWeb3ProtocolURLToWeb2URL = convertPotentialWeb3ProtocolURLToWeb2URL;
//# sourceMappingURL=urls.js.map